
import React from 'react';
import { TeamRecord } from '../types';
import { calculateHomeStats, calculateAwayStats } from '@/utils/leagueStats';
import { calculateTeamStats } from '@/utils/teamStats';

interface LeagueTableStatsProps {
  data: any[];
  viewType: 'all' | 'home' | 'away';
  timeType: 'fullTime' | 'halfTime';
  pointsDeductions?: { team: string; points: number; season: string }[];
}

export const useLeagueTableStats = ({ 
  data, 
  viewType, 
  timeType,
  pointsDeductions = [] 
}: LeagueTableStatsProps) => {
  let stats;
  if (timeType === 'halfTime') {
    switch (viewType) {
      case 'home':
        stats = calculateHalfTimeStats(data, true);
        break;
      case 'away':
        stats = calculateHalfTimeStats(data, false);
        break;
      default:
        stats = calculateAllHalfTimeStats(data);
    }
  } else {
    switch (viewType) {
      case 'home':
        stats = calculateHomeStats(data);
        break;
      case 'away':
        stats = calculateAwayStats(data);
        break;
      default:
        stats = calculateTeamStats(data);
    }
  }

  // Get the season from the first match in the data
  const currentSeason = data[0]?.seasonYear;

  // Apply points deductions for the current league
  return stats.map(team => {
    const deduction = pointsDeductions.find(d => d.team === team.team);
    if (deduction) {
      console.log(`Applying ${deduction.points} points deduction to ${team.team}`);
      return {
        ...team,
        points: team.points - deduction.points,
        ppg: (team.points - deduction.points) / team.played
      };
    }
    return {
      ...team,
      ppg: team.points / team.played
    };
  }).sort((a, b) => 
    b.points - a.points || 
    b.goalDifference - a.goalDifference || 
    b.goalsFor - a.goalsFor
  );
};

export const calculateHalfTimeStats = (matches: any[], isHome: boolean) => {
  const thresholds = [0.5, 1.5, 2.5, 3.5, 4.5, 5.5];
  
  const stats = new Map<string, TeamRecord>();
  
  matches.forEach(match => {
    const team = isHome ? match.HomeTeam : match.AwayTeam;
    if (!stats.has(team)) {
      stats.set(team, {
        team,
        played: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        goalDifference: 0,
        points: 0,
        ppg: 0,
        cleanSheets: 0,
        cleanSheetsPercentage: 0,
        btts: 0,
        bttsPercentage: 0,
        yellowCards: 0,
        redCards: 0,
        over15: 0,
        over15Percentage: 0,
        over25: 0,
        over25Percentage: 0
      });
    }

    const record = stats.get(team)!;
    record.played++;
    
    const homeGoals = parseInt(match.HTHG) || 0;
    const awayGoals = parseInt(match.HTAG) || 0;
    const totalGoals = homeGoals + awayGoals;
    
    if (isHome) {
      record.yellowCards += parseInt(match.HY) || 0;
      record.redCards += parseInt(match.HR) || 0;
      record.goalsFor += homeGoals;
      record.goalsAgainst += awayGoals;
      if (awayGoals === 0) {
        record.cleanSheets++;
      }
      if (homeGoals > 0 && awayGoals > 0) {
        record.btts++;
      }
    } else {
      record.yellowCards += parseInt(match.AY) || 0;
      record.redCards += parseInt(match.AR) || 0;
      record.goalsFor += awayGoals;
      record.goalsAgainst += homeGoals;
      if (homeGoals === 0) {
        record.cleanSheets++;
      }
      if (homeGoals > 0 && awayGoals > 0) {
        record.btts++;
      }
    }

    if (totalGoals > 1.5) {
      record.over15++;
    }
    if (totalGoals > 2.5) {
      record.over25++;
    }
    
    record.goalDifference = record.goalsFor - record.goalsAgainst;

    const result = match.HTR;
    if (isHome) {
      if (result === 'H') {
        record.wins++;
        record.points += 3;
      } else if (result === 'A') {
        record.losses++;
      } else {
        record.draws++;
        record.points += 1;
      }
    } else {
      if (result === 'A') {
        record.wins++;
        record.points += 3;
      } else if (result === 'H') {
        record.losses++;
      } else {
        record.draws++;
        record.points += 1;
      }
    }

    record.ppg = record.points / record.played;
    record.cleanSheetsPercentage = (record.cleanSheets / record.played) * 100;
    record.bttsPercentage = (record.btts / record.played) * 100;
    record.over15Percentage = (record.over15 / record.played) * 100;
    record.over25Percentage = (record.over25 / record.played) * 100;

    stats.set(team, record);
  });

  return Array.from(stats.values());
};

export const calculateAllHalfTimeStats = (matches: any[]) => {
  const thresholds = [0.5, 1.5, 2.5, 3.5, 4.5, 5.5];
  
  const stats = new Map<string, TeamRecord>();
  
  matches.forEach(match => {
    [match.HomeTeam, match.AwayTeam].forEach(team => {
      if (!stats.has(team)) {
        stats.set(team, {
          team,
          played: 0,
          wins: 0,
          draws: 0,
          losses: 0,
          goalsFor: 0,
          goalsAgainst: 0,
          goalDifference: 0,
          points: 0,
          ppg: 0,
          cleanSheets: 0,
          cleanSheetsPercentage: 0,
          btts: 0,
          bttsPercentage: 0,
          yellowCards: 0,
          redCards: 0,
          over15: 0,
          over15Percentage: 0,
          over25: 0,
          over25Percentage: 0
        });
      }
    });

    const homeStats = stats.get(match.HomeTeam)!;
    const awayStats = stats.get(match.AwayTeam)!;
    
    homeStats.played++;
    awayStats.played++;
    
    const homeGoals = parseInt(match.HTHG) || 0;
    const awayGoals = parseInt(match.HTAG) || 0;
    const totalGoals = homeGoals + awayGoals;
    
    // Update cards
    homeStats.yellowCards += parseInt(match.HY) || 0;
    homeStats.redCards += parseInt(match.HR) || 0;
    awayStats.yellowCards += parseInt(match.AY) || 0;
    awayStats.redCards += parseInt(match.AR) || 0;
    
    homeStats.goalsFor += homeGoals;
    homeStats.goalsAgainst += awayGoals;
    awayStats.goalsFor += awayGoals;
    awayStats.goalsAgainst += homeGoals;
    
    if (awayGoals === 0) {
      homeStats.cleanSheets++;
    }
    if (homeGoals === 0) {
      awayStats.cleanSheets++;
    }

    if (homeGoals > 0 && awayGoals > 0) {
      homeStats.btts++;
      awayStats.btts++;
    }

    if (totalGoals > 1.5) {
      homeStats.over15++;
      awayStats.over15++;
    }
    if (totalGoals > 2.5) {
      homeStats.over25++;
      awayStats.over25++;
    }
    
    homeStats.goalDifference = homeStats.goalsFor - homeStats.goalsAgainst;
    awayStats.goalDifference = awayStats.goalsFor - awayStats.goalsAgainst;

    if (match.HTR === 'H') {
      homeStats.wins++;
      homeStats.points += 3;
      awayStats.losses++;
    } else if (match.HTR === 'A') {
      awayStats.wins++;
      awayStats.points += 3;
      homeStats.losses++;
    } else {
      homeStats.draws++;
      awayStats.draws++;
      homeStats.points += 1;
      awayStats.points += 1;
    }

    homeStats.ppg = homeStats.points / homeStats.played;
    awayStats.ppg = awayStats.points / awayStats.played;
    
    homeStats.cleanSheetsPercentage = (homeStats.cleanSheets / homeStats.played) * 100;
    awayStats.cleanSheetsPercentage = (awayStats.cleanSheets / awayStats.played) * 100;

    homeStats.bttsPercentage = (homeStats.btts / homeStats.played) * 100;
    awayStats.bttsPercentage = (awayStats.btts / awayStats.played) * 100;

    homeStats.over15Percentage = (homeStats.over15 / homeStats.played) * 100;
    awayStats.over15Percentage = (awayStats.over15 / awayStats.played) * 100;
    homeStats.over25Percentage = (homeStats.over25 / homeStats.played) * 100;
    awayStats.over25Percentage = (awayStats.over25 / awayStats.played) * 100;

    stats.set(match.HomeTeam, homeStats);
    stats.set(match.AwayTeam, awayStats);
  });

  return Array.from(stats.values());
};
